import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Sparkles, ArrowRight } from "lucide-react";

export default function Auth() {
  const [params] = useSearchParams();
  const initialMode = params.get("mode") === "register" ? "register" : "login";
  const [mode, setMode] = useState(initialMode);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/studio");
  }, [user, navigate]);

  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "register") {
        await register(name, email, password);
        toast.success("Welcome to StyleMe. Enjoy 3 free looks on us.");
      } else {
        await login(email, password);
        toast.success("Welcome back.");
      }
      navigate("/studio");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/studio";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#0F0E13] text-[#F7F5F2] grid lg:grid-cols-2">
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/7a599ca6-7796-4e0c-beec-5e352f4875f1/images/05a4f46ff9d0b51be14ea1acd44f0a3a62b808261f28b54fd31dba461e658db3.png"
          alt="Editorial"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F0E13]/40 to-transparent" />
        <Link to="/" className="absolute top-10 left-10 flex items-center gap-2" data-testid="auth-logo-link">
          <Sparkles className="w-4 h-4 text-[#E3C2B0]" strokeWidth={1.5} />
          <span className="font-serif text-2xl text-[#F7F5F2]">StyleMe</span>
        </Link>
        <div className="absolute bottom-12 left-10 right-10">
          <p className="font-serif text-3xl text-[#F7F5F2] leading-tight italic">
            "Beauty begins the moment you decide to be yourself."
          </p>
          <p className="text-xs uppercase tracking-[0.3em] text-[#E3C2B0] mt-4">— Coco Chanel</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md fade-up">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-12" data-testid="auth-logo-link-mobile">
            <Sparkles className="w-4 h-4 text-[#E3C2B0]" strokeWidth={1.5} />
            <span className="font-serif text-2xl">StyleMe</span>
          </Link>
          <span className="text-xs uppercase tracking-[0.4em] text-[#E3C2B0]">
            {mode === "register" ? "New Member" : "Returning"}
          </span>
          <h1 className="font-serif text-5xl mt-4 font-light leading-tight">
            {mode === "register" ? "Begin your atelier." : "Welcome back."}
          </h1>

          <form onSubmit={submit} className="mt-12 space-y-8">
            {mode === "register" && (
              <Field
                label="Name"
                type="text"
                value={name}
                onChange={setName}
                required
                testid="auth-name"
              />
            )}
            <Field label="Email" type="email" value={email} onChange={setEmail} required testid="auth-email" />
            <Field
              label="Password"
              type="password"
              value={password}
              onChange={setPassword}
              required
              minLength={6}
              testid="auth-password"
            />

            <button
              type="submit"
              disabled={loading}
              data-testid="auth-submit"
              className="w-full bg-[#E3C2B0] text-[#0A0908] py-4 rounded-sm hover:bg-[#F0D4C5] transition-colors text-xs uppercase tracking-[0.3em] disabled:opacity-50 flex items-center justify-center gap-3 group"
            >
              {loading ? "Working..." : mode === "register" ? "Create Account" : "Sign In"}
              {!loading && <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />}
            </button>

            {mode === "login" && (
              <Link
                to="/auth/forgot"
                data-testid="auth-forgot-link"
                className="block text-center text-xs uppercase tracking-[0.3em] text-[#A8A6A1] hover:text-[#E3C2B0] transition-colors -mt-4"
              >
                Forgot password?
              </Link>
            )}
          </form>

          <div className="mt-8 flex items-center gap-4">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#A8A6A1]">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <button
            type="button"
            onClick={googleSignIn}
            data-testid="auth-google-btn"
            className="mt-8 w-full border border-white/15 hover:border-[#E3C2B0] text-[#F7F5F2] py-4 rounded-sm transition-colors text-xs uppercase tracking-[0.3em] flex items-center justify-center gap-3"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47a5.57 5.57 0 0 1-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z" />
              <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09A12 12 0 0 0 12 24z" />
              <path fill="#FBBC05" d="M5.27 14.29A7.21 7.21 0 0 1 4.89 12c0-.8.14-1.57.38-2.29V6.62H1.29a12 12 0 0 0 0 10.76l3.98-3.09z" />
              <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42A11.97 11.97 0 0 0 12 0 12 12 0 0 0 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" />
            </svg>
            Continue with Google
          </button>

          <div className="mt-8 text-center text-[11px] uppercase tracking-[0.3em] text-[#A8A6A1]">
            3 free looks on the house · No card required
          </div>

          <button
            data-testid="auth-toggle"
            onClick={() => setMode(mode === "register" ? "login" : "register")}
            className="mt-8 text-xs uppercase tracking-[0.3em] text-[#A8A6A1] hover:text-[#F7F5F2] transition-colors"
          >
            {mode === "register" ? "Already a member? Sign in" : "New here? Create an account"}
          </button>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, type, value, onChange, required, minLength, testid }) => (
  <div>
    <label className="text-xs uppercase tracking-[0.3em] text-[#A8A6A1] block mb-3">{label}</label>
    <input
      type={type}
      required={required}
      minLength={minLength}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full bg-transparent border-b border-white/15 focus:border-[#E3C2B0] outline-none py-3 text-[#F7F5F2] transition-colors text-lg font-light"
    />
  </div>
);
